import type { GridQuery } from '../api/client'

/**
 * A sortable column heading.
 *
 * Three states, not two: clicking cycles ascending → descending → off. Without
 * the third the user can reorder the register but never get back to the default
 * view except by editing the URL, and "undo that" is a thing people want after
 * every exploratory click.
 *
 * The sort is a server-side ORDER BY, not a client-side sort of the current page.
 * Sorting 40 visible rows out of 610 would produce a page that is ordered but
 * wrong — the most overdue vehicle in the fleet is very unlikely to be on page 1
 * before the sort is applied.
 */
export function SortHeader({
  label,
  sortKey,
  query,
  setQuery,
  numeric,
  title,
}: {
  label: string
  sortKey: string
  query: GridQuery
  setQuery: (next: (prev: GridQuery) => GridQuery) => void
  /** Right-aligns the label, for the date columns. */
  numeric?: boolean
  title?: string
}) {
  const active = query.sortBy === sortKey
  const descending = active && query.sortDescending === true

  function cycle() {
    setQuery((q) => {
      if (q.sortBy !== sortKey) return { ...q, sortBy: sortKey, sortDescending: undefined, page: 1 }
      if (!q.sortDescending) return { ...q, sortBy: sortKey, sortDescending: true, page: 1 }
      return { ...q, sortBy: undefined, sortDescending: undefined, page: 1 }
    })
  }

  return (
    <th
      // A plain class rather than relying on `th:has(.sortth)` in the stylesheet.
      // The padding and overflow rules it carries are what keep the caret from
      // being clipped, and a selector that silently does nothing on an older
      // engine would take the sort affordance with it.
      className={`sortable${numeric ? ' dt' : ''}`}
      // aria-sort belongs on the header cell, not the button: it describes the
      // column's state, and assistive tech reads it from the <th>.
      aria-sort={active ? (descending ? 'descending' : 'ascending') : 'none'}
    >
      <button
        type="button"
        className={`sortth${active ? ' sortth--on' : ''}`}
        onClick={cycle}
        title={title ?? `Sort by ${label.toLowerCase()}`}
      >
        <span>{label}</span>
        {/* Always rendered, faint when inactive: a caret that appears only on the
            sorted column leaves no clue the others can be sorted at all. */}
        <i className="sortth__caret" aria-hidden="true">
          {active ? (descending ? '▼' : '▲') : '↕'}
        </i>
      </button>
    </th>
  )
}
